/**
 * Sales daily queue view per design README section 5.6 and spec
 * section 15.9. Renders four metric tiles, the commit ritual (when no
 * commits are recorded yet for today), the committed section (with the
 * orange left rail), the on-deck section (ranked by importance), and
 * the 3pm uncleared-queue banner.
 *
 * Each row carries the inline actions: mark done, snooze 1 day, snooze
 * 1 week, disqualify. Marking done logs the right SalesAction against
 * the underlying lead or opp via the existing /log-action endpoint;
 * snooze patches `nextActionDue`; disqualify routes through the
 * existing /disqualify endpoint.
 */

import { useState } from 'react';
import { CheckIcon, ClockIcon, NoSymbolIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import {
  useSalesQueue,
  useCommitQueue,
  useMorningBrief,
  useDisqualifyLead,
  useUpdateLead,
  useUpdateOpportunity,
  useLogLeadAction,
  type WireQueueItem,
} from '@/lib/api/sales';
import { useSlideOver } from '@/lib/slide-over';
import { cn } from '@/lib/utils';

const REASON_LABELS: Record<WireQueueItem['reason'], string> = {
  STALE: 'NEEDS ACTION',
};

export function SalesQueue() {
  const queueQuery = useSalesQueue();
  const briefQuery = useMorningBrief();
  const commitQueue = useCommitQueue();
  const openSlideOver = useSlideOver((s) => s.open);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  if (queueQuery.isLoading || briefQuery.isLoading) {
    return <p className="mono-label text-mx-grey-mid">LOADING…</p>;
  }
  if (queueQuery.isError || briefQuery.isError) {
    return <p className="text-mx-body-sm text-mx-neg">Could not load the queue.</p>;
  }

  const onDeck = queueQuery.data?.items ?? [];
  const committed = queueQuery.data?.committed ?? [];
  const brief = briefQuery.data;
  const dailyCommitTarget = brief?.dailyCommitTarget ?? 3;
  const showCommitRitual = committed.length === 0 && onDeck.length > 0;

  // Pre-select the top N items per spec section 5.9.
  const suggested = onDeck.slice(0, dailyCommitTarget);
  const suggestedIds = new Set(suggested.map((i) => i.id));

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function onCommit() {
    const ids = selected.size > 0 ? Array.from(selected) : Array.from(suggestedIds);
    commitQueue.mutate(ids, {
      onSuccess: () => setSelected(new Set()),
    });
  }

  return (
    <div className="flex flex-col gap-mx-4">
      <MetricTiles
        committed={committed.length}
        onDeck={onDeck.length}
        target={dailyCommitTarget}
        zeroDayYesterday={brief?.zeroDayFlag ?? false}
      />

      {brief?.threePmBanner && <ThreePmBanner uncleared={onDeck.length} />}

      {showCommitRitual && (
        <CommitRitual
          suggested={suggested}
          selected={selected}
          suggestedIds={suggestedIds}
          toggle={toggleSelected}
          onCommit={onCommit}
          pending={commitQueue.isPending}
        />
      )}

      {committed.length > 0 && (
        <Section eyebrowNum="01" title="Committed today">
          <ul className="flex flex-col">
            {committed.map((item) => (
              <li key={item.id}>
                <QueueRow item={item} committed openSlideOver={openSlideOver} />
              </li>
            ))}
          </ul>
        </Section>
      )}

      {onDeck.length > 0 && (
        <Section
          eyebrowNum={committed.length > 0 ? '02' : '01'}
          title="On deck · ranked by urgency"
        >
          <ul className="flex flex-col">
            {onDeck.map((item) => (
              <li key={item.id}>
                <QueueRow item={item} openSlideOver={openSlideOver} />
              </li>
            ))}
          </ul>
        </Section>
      )}

      {onDeck.length === 0 && committed.length === 0 && (
        <p className="text-mx-body-sm text-mx-grey-mid">
          Inbox zero. No sales actions need attention right now.
        </p>
      )}
    </div>
  );
}

function MetricTiles({
  committed,
  onDeck,
  target,
  zeroDayYesterday,
}: {
  committed: number;
  onDeck: number;
  target: number;
  zeroDayYesterday: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-mx-2 lg:grid-cols-4">
      <Tile
        label="TODAY'S QUEUE"
        value={(committed + onDeck).toString()}
        sub={`${committed} committed · ${onDeck} on deck`}
      />
      <Tile label="COMMIT TARGET" value={target.toString()} sub="Recommended commits" />
      <Tile
        label="ON DECK"
        value={onDeck.toString()}
        sub={onDeck > 0 ? 'Awaiting commit' : 'Cleared'}
      />
      <Tile
        label="YESTERDAY"
        value={zeroDayYesterday ? 'ZERO' : '✓'}
        sub={zeroDayYesterday ? 'Zero actions logged' : 'Actions logged'}
        tone={zeroDayYesterday ? 'warn' : 'default'}
      />
    </div>
  );
}

function Tile({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'default' | 'warn';
}) {
  return (
    <div className="flex flex-col gap-mx-1 border border-mx-grey-800 bg-mx-card px-mx-3 py-mx-3">
      <p className="mono-label text-mx-grey-mid">{label}</p>
      <p
        className={cn(
          'tnum font-display text-mx-h2 font-semibold',
          tone === 'warn' ? 'text-mx-orange' : 'text-mx-grey-300',
        )}
      >
        {value}
      </p>
      <p className="text-mx-body-sm text-mx-grey-mid">{sub}</p>
    </div>
  );
}

function ThreePmBanner({ uncleared }: { uncleared: number }) {
  return (
    <div
      role="status"
      data-testid="three-pm-banner"
      className="flex items-center justify-between border border-mx-grey-800 border-l-[3px] border-l-mx-orange bg-mx-card px-mx-4 py-mx-3"
    >
      <span className="text-mx-body-sm text-mx-grey-300">
        <span className="mono-label mr-mx-2 text-mx-orange">3PM CHECK</span>
        {uncleared} sales actions still uncleared. Worth a 20-minute pass before close of day.
      </span>
    </div>
  );
}

function CommitRitual({
  suggested,
  selected,
  suggestedIds,
  toggle,
  onCommit,
  pending,
}: {
  suggested: WireQueueItem[];
  selected: Set<string>;
  suggestedIds: Set<string>;
  toggle: (id: string) => void;
  onCommit: () => void;
  pending: boolean;
}) {
  const effectiveSelected = selected.size > 0 ? selected : suggestedIds;
  return (
    <section data-testid="commit-ritual" className="border border-mx-grey-800 bg-mx-card p-mx-4">
      <p className="mono-label text-mx-grey-mid">
        <span className="num">00</span>
        Commit today&rsquo;s actions
      </p>
      <p className="mt-mx-2 text-mx-body-sm text-mx-grey-mid">
        The top {suggested.length} items are pre-selected. Swap any in or out, then commit.
        Committed items get an orange rail and feed the inbox-zero streak.
      </p>
      <ul className="mt-mx-3 flex flex-col">
        {suggested.map((item) => {
          const checked = effectiveSelected.has(item.id);
          return (
            <li
              key={item.id}
              className="flex items-center gap-mx-3 border-t border-mx-grey-800 py-mx-2 first:border-t-0"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(item.id)}
                aria-label={`Commit ${item.companyName}`}
                className="h-4 w-4 accent-mx-orange"
              />
              <span className="mono-label text-mx-grey-mid">{REASON_LABELS[item.reason]}</span>
              <span className="text-mx-body-sm text-mx-grey-300">
                {item.companyName}
                {item.opportunityName ? ` · ${item.opportunityName}` : ''}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="mt-mx-3 flex justify-end">
        <button
          type="button"
          onClick={onCommit}
          disabled={pending}
          className="h-9 bg-mx-orange px-mx-3 text-mx-body-sm font-medium text-mx-white hover:bg-mx-orange-hover disabled:bg-mx-grey-mid"
        >
          {pending ? 'Committing…' : 'Commit'}
        </button>
      </div>
    </section>
  );
}

function Section({
  eyebrowNum,
  title,
  children,
}: {
  eyebrowNum: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <p className="mono-label text-mx-grey-mid">
        <span className="num">{eyebrowNum}</span>
        {title}
      </p>
      <div className="mt-mx-3 border border-mx-grey-800 bg-mx-card">{children}</div>
    </section>
  );
}

type OpenSlideOver = (
  target:
    | { kind: 'task'; taskId: string }
    | { kind: 'lead'; leadId: string }
    | { kind: 'opportunity'; opportunityId: string },
) => void;

function QueueRow({
  item,
  committed = false,
  openSlideOver,
}: {
  item: WireQueueItem;
  committed?: boolean;
  openSlideOver: OpenSlideOver;
}) {
  const logLeadAction = useLogLeadAction();
  const updateLead = useUpdateLead();
  const updateOpportunity = useUpdateOpportunity();
  const disqualifyLead = useDisqualifyLead();

  function snoozeDays(days: number) {
    // Tabs-updates: the queue is staleness-based; snoozing defers the
    // record via `snoozeUntil` (ISO datetime) so it leaves the queue until
    // then. Replaces the old nextActionDue snooze.
    const until = new Date();
    until.setDate(until.getDate() + days);
    const iso = until.toISOString();
    if (item.kind === 'lead') {
      updateLead.mutate({ id: item.id, patch: { snoozeUntil: iso } });
    } else {
      updateOpportunity.mutate({ id: item.id, patch: { snoozeUntil: iso } });
    }
  }

  function markDone() {
    if (item.kind === 'lead') {
      // Log the suggested NextAction. The state machine moves the lead
      // forward (resetting lastActionAt) so the row leaves the queue.
      logLeadAction.mutate({
        id: item.id,
        body: { action: 'FOLLOW_UP', summary: 'Marked done from queue' },
      });
    } else {
      // Opportunities have no generic log-action endpoint; mark done
      // snoozes the row a day so it leaves the queue.
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      updateOpportunity.mutate({
        id: item.id,
        patch: { snoozeUntil: tomorrow.toISOString() },
      });
    }
  }

  function disqualify() {
    if (item.kind === 'lead') {
      disqualifyLead.mutate({ id: item.id });
    }
  }

  return (
    <div
      data-testid="queue-row"
      data-queue-id={item.id}
      className={cn(
        'group flex items-center gap-mx-3 border-t border-mx-grey-800 px-mx-4 py-mx-3 first:border-t-0',
        committed && 'border-l-[3px] border-l-mx-orange',
      )}
    >
      <button
        type="button"
        onClick={() =>
          openSlideOver(
            item.kind === 'lead'
              ? { kind: 'lead', leadId: item.id }
              : { kind: 'opportunity', opportunityId: item.id },
          )
        }
        className="flex flex-1 items-center gap-mx-3 text-left"
      >
        <span className="mono-label w-[140px] shrink-0 text-mx-grey-mid">
          {REASON_LABELS[item.reason]}
        </span>
        <span className="flex-1 text-mx-body-sm text-mx-grey-300">
          <strong className="font-medium">{item.companyName}</strong>
          {item.opportunityName && (
            <span className="ml-mx-2 text-mx-grey-mid">· {item.opportunityName}</span>
          )}
          {item.contactName && (
            <span className="ml-mx-2 text-mx-grey-mid">· {item.contactName}</span>
          )}
        </span>
        <span className="mono-label w-[180px] shrink-0 text-mx-grey-mid">
          {item.suggestedAction}
        </span>
        <span className="tnum w-[48px] shrink-0 text-right text-mx-body-sm text-mx-grey-mid">
          {item.ageDays}d
        </span>
        <ChevronRightIcon
          className="h-4 w-4 shrink-0 text-mx-grey-mid"
          strokeWidth={1.6}
          aria-hidden
        />
      </button>
      <div className="flex items-center gap-mx-1">
        <RowAction
          aria-label="Mark done"
          onClick={markDone}
          icon={<CheckIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />}
        />
        <RowAction
          aria-label="Snooze 1 day"
          onClick={() => snoozeDays(1)}
          icon={<ClockIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />}
          label="1D"
        />
        <RowAction
          aria-label="Snooze 1 week"
          onClick={() => snoozeDays(7)}
          icon={<ClockIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />}
          label="1W"
        />
        {item.kind === 'lead' && (
          <RowAction
            aria-label="Disqualify"
            onClick={disqualify}
            icon={<NoSymbolIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />}
          />
        )}
      </div>
    </div>
  );
}

function RowAction({
  onClick,
  icon,
  label,
  ...rest
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label?: string;
} & React.AriaAttributes) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-7 items-center gap-mx-1 border border-mx-grey-800 bg-mx-grey-900 px-mx-2 text-mx-mono-sm text-mx-grey-mid transition-colors hover:border-mx-orange hover:text-mx-orange"
      {...rest}
    >
      {icon}
      {label && <span className="mono-label">{label}</span>}
    </button>
  );
}
